// TypeScript declarations for global window properties
declare global {
    interface Window {
        fbq?: (...args: any[]) => void & {
            __fbeventsModules?: {
                fbevents?: () => {
                    getState?: () => { pixels: { id: string }[] };
                };
            };
        };
        _fbq?: any;
        dataLayer?: any[];
    }
}
import { uuid } from "uuidv4";
import { getMovieAttributes, isElementInViewport } from "./misc";
import { CinemaType, MovieType, SessionType } from "../types/entities";
import { getCandybarItemModifiersNames, getCandybarTransactionModifiersArray } from "./candybar";
import { trackEvent } from "./analytics/serverSide";


type PromoType = 'promo_a' | 'promo_featured' | 'promo_wide' | 'featured_movies_promo' | string;
type AnalyticsPromosObserved = {
    type: PromoType;
    id: number;
    description: string;
    element: Element;
};

interface AnalyticsPromosInterface {
    observer?: IntersectionObserver;
    observed: AnalyticsPromosObserved[];
    trackView(type: PromoType, id: number, description: string, element: Element | null): void;
    trackClick(type: PromoType, id: number, description: string, callback?: (() => void) | null): void;
    init(): void;
    observe(type: PromoType, id: number, description: string, element: Element | null): void;
    getObserved(element: Element): AnalyticsPromosObserved | undefined;
    reset(type: PromoType): void;
    resetAll(): void;
}

let trackedPromos: { [key: string]: number[] } = {};

const isAlreadyTracked = (type: string, id: number) => {
    return (type in trackedPromos) && trackedPromos[type].indexOf(id) > -1;
};

const mapPromoTypeToNewNames = (type: string) => {
    const newNames: Record<string, string> = {
        'promo_a': 'destacada',
        'promo_featured': 'secundaria',
        'promo_wide': 'ancha',
        'featured_movies_promo': 'peliculas_destacadas',
    };

    return (type in newNames) ? newNames[type] : type;
};

export const AnalyticsPromos: AnalyticsPromosInterface = {
    observed: [],
    trackView(type, id, description, element) {
        if (isAlreadyTracked(type, id)) return;
        if (!isElementInViewport(element)) return;

        if (!(type in trackedPromos)) trackedPromos[type] = [];

        trackedPromos[type].push(id);
        dataLayerPush({
            event: 'promoView',
            type: mapPromoTypeToNewNames(type),
            id,
            description,
        });
    },

    trackClick(type, id, description, callback) {
        dataLayerPush({
            event: 'promoClick',
            type: mapPromoTypeToNewNames(type),
            id,
            description,
            eventCallback: callback,
            eventTimeout: 2000
        });
    },

    init() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                
                const observed = this.getObserved(entry.target);
                if (!observed) return;
                const {type, id, description, element} = observed;
                this.trackView(type, id, description, element);
            });
        }, {threshold: 1});
        this.observed = [];
    },

    observe(type, id, description, element) {
        if (!element) return;
        if (!this.observer) this.init();
        if (!this.observer) return;

        this.observer.observe(element);
        this.observed.push({
            type,
            id,
            description,
            element
        });
    },

    getObserved(element: Element): AnalyticsPromosObserved | undefined {
        for (let i = 0; i < this.observed.length; i++) {
            if (this.observed[i].element === element) {
                return this.observed[i];
            }
        }
    },

    reset(type) {
        trackedPromos[type] = [];
    },

    resetAll() {
        trackedPromos = {};
    }
};

export const AnalyticsModals = {
    trackView(id, description, placement) {
        dataLayerPush({
            event: 'modalView',
            id,
            description,
            placement
        });
    },

    trackClose(id, description, placement) {
        dataLayerPush({
            event: 'modalClose',
            id,
            description,
            placement
        });
    },
    
    trackClick(id, description, placement, callback) {
        dataLayerPush({
            event: 'modalClick',
            id,
            description,
            placement,
            eventCallback: callback,
            eventTimeout: 2000
        });
    }
};

export const AnalyticsCheckout = {
    trackTicketAdd(ticket) {
        dataLayerPush({
            event: 'checkoutTicketAdd',
            data: {
                ticketPrice: ticket.price,
                ticketName: ticket.name,
            }
        });
    },

    trackTicketRemove(ticket) {
        dataLayerPush({
            event: 'checkoutTicketRemove',
            data: {
                ticketPrice: ticket.price,
                ticketName: ticket.name,
            }
        });
    },

    trackStep(step, data) {
        // @todo Replace step names strings with constants/variables.
        const newIdToOldIdMap = {
            'tickets': 'select-tickets',
            'seats': 'select-seats',
            'candybar': 'candybar',
            'payment-suggestions': 'select-payment',
            'payment': 'payment',
            'confirmation': 'confirmation'
        };
        dataLayerPush({
            event: 'checkoutChangeStep',
            eventId: data.eventId || undefined,
            step: (step in newIdToOldIdMap) ? newIdToOldIdMap[step] : step,
            data: {
                session: {
                    id: data.session.id,
                    dateTime: data.session.datetime,
                    date: data.session.datetime.match(/^([0-9]{4}-[0-9]{2}-[0-9]{2})T/)[1],
                    time: data.session.datetime.match(/T([0-9]{2}:[0-9]{2}:[0-9]{2})/)[1],
                },
                cinema: {
                    id: data.cinema.id,
                    name: data.cinema.name,
                },
                movie: {
                    id: data.movie.parent_id,
                    versionId: data.movie.id,
                    title: data.movie.name,
                    genre: data.movie.info.genre,
                    ageRating: data.movie.info.rating,
                    director: data.movie.info.director,
                    cast: data.movie.info.cast,
                    country: data.movie.info.country,
                    year: data.movie.info.year,
                    format: data.movie.label,
                    url: 'https:' + data.movie.url,
                    poster_url: 'https:' + data.movie.poster_big,
                },
                order: parseOrderData(data.order),
                transaction: data.transaction,
            }
        });

        fbTrackCheckoutStep(step, data);
    },

    trackTransaction(transaction) {
        const products: { [key: string]: any }[] = [];

        if (transaction.tickets) {
            transaction.tickets.forEach(ticket => {
                products.push({
                    name: 'Boleto',
                    sku: ticket.type,
                    category: ticket.name,
                    price: ticket.price / 100,
                    quantity: ticket.qty
                });
            });
        }

        if (transaction.candybar && transaction.candybar.summary) {
            transaction.candybar.summary.forEach(item => {
                products.push({
                    name: item.name,
                    sku: item.id,
                    category: 'Dulcería',
                    price: item.price / 100,
                    quantity: 1,
                    modifiers: getCandybarTransactionModifiersArray(item.configs).join(' | ') || null
                });
            });
        }

        dataLayerPush({
            event: 'checkoutComplete',
            transactionId: transaction.id,
            transactionAffiliation: transaction.cinema.name,
            transactionTotal: ('cash' in transaction.payment.methods) ? (transaction.payment.methods.cash / 100) : 0,
            transactionShipping: 0,
            transactionTax: 0,
            transactionProducts: products
        });
    }
}

function parseOrderData(order) {
    const result = {
        tickets: [],
        candybar: []
    };

    if (!order) return result;

    if (order.tickets) {
        result.tickets = order.tickets.filter(ticket => ticket.qty >= 1).map(ticket => ({
            type: ticket.id,
            name: ticket.name,
            qty: ticket.qty,
            price: ticket.price,
        }));
    }

    if (order.candybar && order.candybar.summary) {
        result.candybar = order.candybar.summary.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            // @todo Add modifiers and category
        }));
    }

    return result;
}

export const AnalyticsBillboards = {
    trackCinema(cinema) {
        if (!cinema) return;

        dataLayerPush({
            event: 'billboardCinemaView',
            action: 'Cinema',
            label: cinema.name,
            data: {
                cinema: {
                    id: cinema.id,
                    name: cinema.name
                }
            }
        });
    },

    trackMovie(movie, cinema) {
        const eventId = uuid();

        if (!movie || !cinema) return;

        dataLayerPush({
            event: 'billboardMovieView',
            eventId,
            action: 'Movie',
            label: `${cinema.name} - ${movie.name}`,
            data: {
                cinema: {
                    id: cinema.id,
                    name: cinema.name
                },
                movie: {
                    id: movie.id,
                    name: movie.name,
                    url: 'https:' + movie.url,
                    poster_url: 'https:' + movie.poster_big,
                }
            }
        });

        const metaEventData = {
            content_ids: 'movie_' + movie.id,
            content_name: movie.name,
            content_type: 'product',
            event_source_url: window.location.href,
            currency: 'MXN',
            value: 0,
            content_category: `Cinema > ${getCinemaExperience(cinema)} > ${movie.info.genre.join(', ')}`,
        };

        addCinemaFieldsToEventData(metaEventData, cinema);
        addMovieFieldsToEventData(metaEventData, movie);
        
        trackEvent(
            'ViewContent',
            eventId,
            metaEventData
        );

        const googleEvent = {
            event: 'view_item',
            currency: 'MXN',
            value: 0,
            items: [
                {
                    item_id: metaEventData.content_ids,
                    item_name: metaEventData.content_name
                }
            ]
        };

        dataLayerPush(googleEvent);
    },

    trackMovieView(movie) {
        dataLayerPush({
            event: 'movieView',
            action: 'Open',
            label: `${movie.name}`,
            data: {
                movie: {
                    id: movie.parent_id,
                    versionId: movie.id,
                    title: movie.name,
                    genre: movie.info.genre,
                    ageRating: movie.info.rating,
                    director: movie.info.director,
                    cast: movie.info.cast,
                    country: movie.info.country,
                    year: movie.info.year,
                    format: movie.label,
                    url: 'https:' + movie.url,
                    poster_url: 'https:' + movie.poster_big,
                },
            }
        });
        movie.extra.fb_pixels.length && fbqTrack(movie.extra.fb_pixels, 'ViewContent', {
            content_ids: movie.id,
            content_name: movie.name,
            content_type: 'product',
            movie_genre: movie.info.genre,
            movie_age_rating: movie.info.rating,
            movie_directors: movie.info.director,
            movie_starring: movie.info.cast,
            movie_country: movie.info.country,
            movie_year: movie.info.year,
            movieref: 'fb_movies'
        });
    },

    trackUpcomingMovieView(movie) {
        dataLayerPush({
            event: 'upcomingMovieView',
            action: 'Open',
            label: `${movie.name}`,
            data: {
                movie: {
                    title: movie.name,
                    genre: movie.info.genre,
                },
            }
        });
    }
};

export const AnalyticsCoupon = {
    trackLanding() {
        dataLayerPush({
            event: 'couponsSection',
        });
    },
    
    trackView(coupon) {
        dataLayerPush({
            event: 'couponView',
            coupon: this.getCouponParams(coupon)
        });
    },

    trackActivation(coupon) {
        dataLayerPush({
            event: 'couponActivation',
            coupon: this.getCouponParams(coupon)
        });
    },

    getCouponParams(coupon) {
        return {
            name: coupon.title,
            categories: coupon.categories,
            activation_limit: coupon.activation_date_limit,
            redeem_date: coupon.redeem_date_limit,
        };
    }
};

export const AnalyticsUsers = {
    trackSignUp() {
        dataLayerPush({
            event: 'userSignUp',
            action: 'Success'
        });
    },

    trackSignIn() {
        dataLayerPush({
            event: 'userSignIn',
            action: 'sign-in'
        });
    },

    trackSignOut() {
        dataLayerPush({
            event: 'userSignOut',
            action: 'sign-out'
        });
    },

    trackIeNipChange() {
        dataLayerPush({
            event: 'ieNipChange',
            action: 'nip-change'
        });
    },

    trackIeLink(origin) {
        dataLayerPush({
            event: 'ieLink',
            action: 'link',
            level: '',
        });
    },

    trackIeSignUp(level) {
        dataLayerPush({
            event: 'ieSignUp',
            action: 'sign-up',
            level
        });
    },

    trackIeMigration(action, level) {
        dataLayerPush({
            event: 'ieMigration',
            action,
            level
        });
    },

    trackIeRenewal(action, level) {
        dataLayerPush({
            event: 'ieRenewal',
            action,
            level
        });
    },

    trackUserInfo(data) {
        dataLayerPush({
            event: 'userInfoPush',
            env: {data},
        });
    },

    trackUserInfoInitialized(data) {
        dataLayerPush({
            event: 'userInfoInitialized',
            env: {data},
        });
    }
}

export const dataLayerPush = data => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
}

export const fbqTrack = (pixelIds, event, data, options = {}) => {
    if (!('fbq' in window)) {
        fbqInject();
    }

    pixelIds.forEach(pixelId => {
        fbqInit(pixelId);

        window.fbq && window.fbq('trackSingle', pixelId, event, data, options);
    });
};

const fbqInject = () => {
    (function(f: any, b: Document, e: string, v: string) {
        let n: any, t: any, s: any;
        if (f.fbq) return;
        n = f.fbq = function() {
            if (n && n.callMethod) {
                n.callMethod.apply(n, arguments);
            } else if (n) {
                n.queue.push(arguments);
            }
        };
        if (!f._fbq) f._fbq = n;
        if (n) {
            n.push = n;
            n.loaded = true;
            n.version = '2.0';
            n.queue = [];
        }
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
            s.parentNode.insertBefore(t, s);
        }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
};

const fbqInit = (pixelId: string) => {
    const loadedIds = getFbLoadedIds();
    const pixelLoaded = loadedIds.indexOf(pixelId) > -1;

    if (!pixelLoaded) {
        window.fbq && window.fbq('init', pixelId);
    }
};

const getFbLoadedIds = (): string[] => {
    let result: string[] = [];
    const fbq = window.fbq as any;
    if (
        fbq &&
        fbq.__fbeventsModules &&
        typeof fbq.__fbeventsModules.fbevents === 'function'
    ) {
        const events = fbq.__fbeventsModules.fbevents();
        if (events && typeof events.getState === 'function') {
            const state = events.getState();
            if (state && Array.isArray(state.pixels)) {
                result = state.pixels.map((pixel: { id: string }) => pixel.id);
            }
        }
    }
    return result;
};

const fbTrackCheckoutStep = (step, data) => {
    const fbEvent = fbGetCheckoutStepEventName(step);
    
    if (!data.movie.extra.fb_pixels.length) return;
    if (!fbEvent) return;

    fbqTrack(data.movie.extra.fb_pixels, fbEvent, {
        content_ids: data.movie.id,
        content_name: data.movie.name,
        content_type: 'product',
        movie_genre: data.movie.info.genre.join(','),
        movie_age_rating: data.movie.info.rating,
        movie_version: data.movie.label,
        session_date: data.session.datetime.match(/^([0-9]{4}-[0-9]{2}-[0-9]{2})T/)[1],
        session_time: data.session.datetime.match(/T([0-9]{2}:[0-9]{2}:[0-9]{2})/)[1],
        session_cinema: data.cinema.name,
        session_id: data.session.id,
        movie_directors: data.movie.info.director,
        movie_starring: data.movie.info.cast,
        movie_country: data.movie.info.country,
        movie_year: data.movie.info.year,
        num_items: 0, // @todo
        value: 0, // @todo
        currency: 'MXN',
        movieref: 'fb_movies'
    });
};

const fbGetCheckoutStepEventName = step => {
    switch (step) {
        case 'seats': return 'AddToCart';
        case 'payment': return 'InitiateCheckout';
        case 'confirmation': return 'Purchase';
        default: return null;
    }
}

export const trackAppStoreRedirect = (store, callback) => {
    dataLayerPush({
        event: 'appStoreRedirect',
        store,
        eventCallback: callback,
        eventTimeout: 2000
    });
};

export const AnalyticsCandybar = {
    trackSectionLoad(cinema: CinemaType, catalog: any[]) {
        const eventId = uuid();

        dataLayerPush({
            event: 'candybarSectionLoad',
            eventId
        });

        const eventData = {
            content_ids: [`candybar_${cinema.id}`],
            content_type: 'product',
            content_name: `Food & Drinks - ${cinema.name}`,
            currency: 'MXN',
            value: 0,
            content_category: `Food & Drinks > ${getCinemaExperience(cinema)}`
        };

        addCinemaFieldsToEventData(eventData, cinema);

        trackEvent(
            'ViewContent',
            eventId,
            eventData
        );

        dataLayerPush({
            event: 'view_item_list',
            currency: 'MXN',
            item_list_name: `Food & Drinks - ${cinema.name}`,
            items: catalog.reduce((acc, category) => {
                return acc.concat(category.items.map(item => ({
                    item_id: `candybar_${item.id}`,
                    item_name: item.name
                })));
            }, [])
        });
    },

    trackProductView(product, category, cinema) {
        const eventId = uuid();

        dataLayerPush({
            event: 'candybarProductView',
            eventId,
            product: {
                id: product.id,
                name: product.name,
                price: product.price,
                modifiers: null,
                category: category ? category.name : null
            }
        });

        const metaEventData = {
            content_ids: `candybar_${product.id}`,
            content_type: 'product',
            content_name: product.name,
            currency: 'MXN',
            value: 0,
            content_category: 'Food & Drinks',
            candybar_category: category ? category.name : null,
        };
        addCinemaFieldsToEventData(metaEventData, cinema);

        trackEvent(
            'ViewContent',
            eventId,
            metaEventData
        );

        const googleEvent = {
            event: 'view_item',
            currency: "MXN",
            value: 0,
            items: [
                {
                    item_id: metaEventData.content_ids,
                    item_name: metaEventData.content_name,
                }
            ]
        };

        dataLayerPush(googleEvent);
    },

    trackAddToCart(product, category, selectedConfig) {
        dataLayerPush({
            event: 'candybarAddToCart',
            product: getCandybarProductParams(product, category, selectedConfig)
        });
    },

    trackUpdateCart(product, category, selectedConfig) {
        dataLayerPush({
            event: 'candybarUpdateCart',
            product: getCandybarProductParams(product, category, selectedConfig)
        });
    },
};

function getCandybarProductParams(product, category, selectedConfig) {
    return {
        id: product.id,
        name: product.name,
        price: product.price,
        modifiers: getCandybarItemModifiersNames(product, selectedConfig),
        category: category ? category.name : null
    };
}


export const AnalyticsAR = {
    trackSectionEntry() {
        dataLayerPush({
            event: 'arSectionEntry',
            test: 'foobar'
        });
    },

  trackLensLoad(lensName, lensId) {
    dataLayerPush({
      event: 'arLensLoad',
      lens: {
        name: lensName,
        id: lensId
      }
    });
  }
};

export function getCinemaExperience(cinema) {
    if (cinema.platinum) return 'Platinum';
    if (cinema.market) return 'Market';
    return 'Traditional';
}

export function addCinemaFieldsToEventData(eventData: { [key: string]: any }, cinema: CinemaType) {
    eventData.cinema_id = `cinema_${cinema.id}`;
    eventData.cinema_state = cinema.state.name;
    eventData.cinema_area = cinema.area.name;
}

export function addMovieFieldsToEventData(eventData: { [key: string]: any }, movie: MovieType) {   
    eventData.movie_ids = `movie_${movie.id}`;
    eventData.movie_genres = movie.info ? movie.info.genre : [];
    eventData.movie_rating = movie.info ? movie.info.rating : null;
}

export function addSessionFieldsToEventData(eventData: { [key: string]: any }, session: SessionType, movie: MovieType) {
    const movieAttributes = getMovieAttributes(movie);
    const sessionFormats = movieAttributes ? movieAttributes.filter(attr => !attr.startsWith('lang_')) : [];
    const sessionLanguages = movieAttributes ? movieAttributes.filter(attr => attr.startsWith('lang_')) : [];
    eventData.session_id = `session_${session.id}`;
    eventData.session_language = sessionLanguages.join(', ');
    eventData.session_attributes = sessionFormats;
}

export function getCandybarOrderContentIds(order): string[] {
    const productIds: string[] = [];

    Object.entries(order).forEach(item => {
        if (!item[1]) return;
        for (let i = 0; i < Object.keys(item[1]).length; i++) {
            productIds.push(`candybar_${item[0]}`);
        }
    });

    return productIds;
}